import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Sparkles,
  Loader2,
  Check,
  ArrowRight,
  AlertCircle,
  Lightbulb,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface SEOSuggestion {
  seo_title: string;
  seo_description: string;
  slug_sugerido: string;
  score_seo: number;
  sugestoes: string[];
}

interface SEOGeneratorProps {
  titulo: string;
  conteudo_html: string;
  resumo?: string;
  categoria?: string;
  currentSeoTitle: string;
  currentSeoDescription: string;
  currentSlug: string;
  onApplySeoTitle: (value: string) => void;
  onApplySeoDescription: (value: string) => void;
  onApplySlug: (value: string) => void;
}

export function SEOGenerator({
  titulo,
  conteudo_html,
  resumo,
  categoria,
  currentSeoTitle,
  currentSeoDescription,
  currentSlug,
  onApplySeoTitle,
  onApplySeoDescription,
  onApplySlug,
}: SEOGeneratorProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [suggestion, setSuggestion] = useState<SEOSuggestion | null>(null);
  const [appliedFields, setAppliedFields] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  const handleGenerate = async () => {
    if (!titulo.trim() || !conteudo_html.trim()) {
      toast({
        title: "Conteúdo necessário",
        description: "Preencha o título e conteúdo antes de gerar SEO.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setSuggestion(null);
    setAppliedFields(new Set());

    try {
      const { data, error } = await supabase.functions.invoke("seo-assistant", {
        body: { titulo, conteudo_html, resumo, categoria },
      });

      if (error) {
        throw new Error(error.message || "Erro ao gerar SEO");
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setSuggestion(data);
      toast({
        title: "SEO gerado com sucesso!",
        description: "Revise as sugestões e aplique as que desejar.",
      });
    } catch (error: any) {
      console.error("SEO generation error:", error);
      toast({
        title: "Erro ao gerar SEO",
        description: error.message || "Tente novamente em alguns instantes.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const applyField = (field: "title" | "description" | "slug") => {
    if (!suggestion) return;

    switch (field) {
      case "title":
        onApplySeoTitle(suggestion.seo_title);
        break;
      case "description":
        onApplySeoDescription(suggestion.seo_description);
        break;
      case "slug":
        onApplySlug(suggestion.slug_sugerido);
        break;
    }

    setAppliedFields((prev) => new Set([...prev, field]));
    toast({
      title: "Aplicado!",
      description: `${field === "title" ? "Título SEO" : field === "description" ? "Meta Description" : "Slug"} atualizado.`,
    });
  };

  const applyAll = () => {
    if (!suggestion) return;

    onApplySeoTitle(suggestion.seo_title);
    onApplySeoDescription(suggestion.seo_description);
    onApplySlug(suggestion.slug_sugerido);

    setAppliedFields(new Set(["title", "description", "slug"]));
    toast({
      title: "Todas as sugestões aplicadas!",
      description: "Título, descrição e slug foram atualizados.",
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-amber-600";
    return "text-red-600";
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return "Excelente";
    if (score >= 60) return "Bom";
    if (score >= 40) return "Regular";
    return "Precisa melhorar";
  };

  const getProgressColor = (score: number) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-amber-500";
    return "bg-red-500";
  };

  return (
    <div className="space-y-4">
      {/* Generate Button */}
      <Button
        onClick={handleGenerate}
        disabled={isLoading}
        className="w-full"
        variant="outline"
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Analisando conteúdo...
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4 mr-2" />
            Gerar SEO com IA
          </>
        )}
      </Button>

      {/* Results */}
      {suggestion && (
        <div className="space-y-4 animate-in fade-in-50 slide-in-from-top-2 duration-300">
          {/* Score */}
          <Card className="border-border bg-muted/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Score SEO</span>
                <span className={cn("text-lg font-bold", getScoreColor(suggestion.score_seo))}>
                  {suggestion.score_seo}/100
                </span>
              </div>
              <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={cn("h-full transition-all duration-500", getProgressColor(suggestion.score_seo))}
                  style={{ width: `${suggestion.score_seo}%` }}
                />
              </div>
              <p className={cn("text-xs mt-1", getScoreColor(suggestion.score_seo))}>
                {getScoreLabel(suggestion.score_seo)}
              </p>
            </CardContent>
          </Card>

          {/* Apply All */}
          <Button onClick={applyAll} className="w-full" size="sm">
            <Check className="h-4 w-4 mr-2" />
            Aplicar Todas as Sugestões
          </Button>

          {/* Title Suggestion */}
          <SuggestionCard
            label="Título SEO"
            current={currentSeoTitle || titulo}
            suggested={suggestion.seo_title}
            maxLength={60}
            applied={appliedFields.has("title")}
            onApply={() => applyField("title")}
          />

          {/* Description Suggestion */}
          <SuggestionCard
            label="Meta Description"
            current={currentSeoDescription || resumo || ""}
            suggested={suggestion.seo_description}
            maxLength={160}
            applied={appliedFields.has("description")}
            onApply={() => applyField("description")}
          />

          {/* Slug Suggestion */}
          <SuggestionCard
            label="Slug"
            current={currentSlug}
            suggested={suggestion.slug_sugerido}
            applied={appliedFields.has("slug")}
            onApply={() => applyField("slug")}
            isSlug
          />

          {/* Suggestions */}
          {suggestion.sugestoes.length > 0 && (
            <Card className="border-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb className="h-4 w-4 text-amber-500" />
                  <span className="text-sm font-medium">Sugestões de Melhoria</span>
                </div>
                <ul className="space-y-2">
                  {suggestion.sugestoes.map((sugestao, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-2 text-sm text-muted-foreground"
                    >
                      <AlertCircle className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                      <span>{sugestao}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

interface SuggestionCardProps {
  label: string;
  current: string;
  suggested: string;
  maxLength?: number;
  applied: boolean;
  onApply: () => void;
  isSlug?: boolean;
}

function SuggestionCard({
  label,
  current,
  suggested,
  maxLength,
  applied,
  onApply,
  isSlug,
}: SuggestionCardProps) {
  const isDifferent = current !== suggested;

  return (
    <Card className={cn("border-border transition-colors", applied && "border-green-500/50 bg-green-50/30")}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">{label}</span>
          {maxLength && (
            <span
              className={cn(
                "text-xs",
                suggested.length > maxLength ? "text-destructive" : "text-muted-foreground"
              )}
            >
              {suggested.length}/{maxLength}
            </span>
          )}
        </div>

        {isDifferent && !applied ? (
          <div className="space-y-2">
            {/* Current */}
            <div className="p-2 bg-muted/50 rounded text-sm text-muted-foreground line-through">
              {isSlug ? `/blog/${current || "..."}` : current || "(vazio)"}
            </div>

            {/* Arrow */}
            <div className="flex justify-center">
              <ArrowRight className="h-4 w-4 text-primary" />
            </div>

            {/* Suggested */}
            <div className="p-2 bg-primary/10 rounded text-sm text-foreground border border-primary/20">
              {isSlug ? `/blog/${suggested}` : suggested}
            </div>

            {/* Apply Button */}
            <Button
              variant="secondary"
              size="sm"
              onClick={onApply}
              className="w-full"
            >
              <Check className="h-3 w-3 mr-1" />
              Aplicar
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="flex-1 p-2 bg-muted/50 rounded text-sm">
              {isSlug ? `/blog/${suggested}` : suggested}
            </div>
            {applied && (
              <Badge variant="secondary" className="bg-green-100 text-green-700">
                <Check className="h-3 w-3 mr-1" />
                Aplicado
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
